/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent } from '@/components/ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import useGetAgent from '@/hooks/useGetAgent';
import getApiBaseUrl from '@/utils/getBaseUrl';
import { useAuth0 } from '@auth0/auth0-react';
import { ArrowRight, ChevronLeft, ChevronRight, Edit } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { WiStars } from 'react-icons/wi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ComparableProperty from './ComparableProperty';
import MultiImageUploader from './MultiImageUploader';
import ValuationModal from './ValuationModal';

// Interface for the comparable search criteria
export interface ComparableSearchCriteria {
  livingSquareFeet: number;
  yearBuilt: number;
  propertyType: string;
  propertyUseCode: string;
  longitude: number;
  latitude: number;
  county?: string;
  state?: string;
  estimatedPropertyValue?: number;
}

// Interface for the API response
interface ApiComparableProperty {
  id: string;
  address: {
    address: string;
    city: string;
    state: string;
    zip: string;
    county: string;
  };
  lastSaleAmount: string;
  lastSaleDate: string;
  squareFeet: number;
  pricePerSquareFoot: number;
  bedrooms: number;
  bathrooms: number;
  yearBuilt: number;
  garage: boolean;
  lotSquareFeet: number;
  basement: boolean;
  propertyType: string;
  airConditioningAvailable: boolean;
  roofMaterial: string | null;
  pool: boolean;
  deck: boolean;
  patio: boolean;
  apn: string;
  propertyUse: string;
  mls: Record<string, any>;
}

// Interface for transformed comparable property
export interface IComparableProperty {
  id: string;
  address: string;
  soldPrice: number;
  soldDate: string;
  livingArea: number;
  pricePerSqft: number;
  bedrooms: number;
  bathrooms: number;
  yearBuilt: number;
  garageType: string;
  lotSize: number;
  lotUnit: string;
  basement: string;
  constructionType: string;
  hvac: string;
  roofMaterial: string;
  pool: boolean;
  patio: boolean;
  apn: string;
  subdivision: string;
  adjustmentValue?: number;
  notes?: string;
  mls: Record<string, any>;
  locationAdjustment?: number;
  livingAreaAdjustment?: number;
  garageAdjustment?: number;
  dateAdjustment?: number;
  conditionAdjustment?: number;
  otherFeaturesAdjustment?: number;
}

// Placeholder data for the sponsoring agent
const sponsoringAgent = {
  name: 'Sarah Johnson',
  phone: '(303) 555-7890',
  email: 'sarah.johnson@realestate.com',
  photo:
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop',
};

// Progress messages to display during loading
const progressMessages = [
  { icon: '🧭', message: 'Locating nearby property sales...' },
  { icon: '📏', message: 'Matching homes by size, age, and location...' },
  { icon: '🏘️', message: 'Checking sales within your neighborhood...' },
  { icon: '🕰️', message: 'Reviewing recent market activity...' },
  { icon: '🧠', message: 'Filtering for the most relevant comps...' },
  { icon: '📬', message: 'Cross-checking public records...' },
  { icon: '📊', message: 'Calculating value adjustments...' },
  { icon: '🔍', message: 'Scanning wider area for similar homes...' },
  { icon: '📦', message: 'Wrapping up your comparison report...' },
  { icon: '✅', message: 'Comps found! Generating results...' },
];

// Transform API response to our component format
const transformApiComparables = (
  apiData: ApiComparableProperty[],
): IComparableProperty[] => {
  return apiData.map((comp) => ({
    id: comp.id,
    address: comp.address.address,
    soldPrice: parseInt(comp.lastSaleAmount) || 0,
    soldDate: comp.lastSaleDate,
    livingArea: comp.squareFeet || 0,
    pricePerSqft: comp.pricePerSquareFoot || 0,
    bedrooms: comp.bedrooms || 0,
    bathrooms: comp.bathrooms || 0,
    yearBuilt: comp.yearBuilt || 0,
    garageType: comp.garage ? 'Attached Garage' : 'No Garage',
    lotSize: comp.lotSquareFeet, // Convert to acres
    lotUnit: 'sq ft',
    basement: comp.basement ? 'Yes' : 'No',
    constructionType: 'Frame', // Default value as not provided in API
    hvac: comp.airConditioningAvailable ? 'Central Air' : 'No AC',
    roofMaterial: comp.roofMaterial || 'Unknown',
    pool: comp.pool || false,
    patio: comp.patio || false,
    apn: comp.apn || '',
    subdivision: comp.propertyUse || '',
    mls: comp.mls,
  }));
};

interface ComparableSalesProps {
  userValuation?: number;
  propertyDataLoaded?: boolean;
  comparableSearchCriteria?: ComparableSearchCriteria | null;
  subjectPropertySqft?: number;
  dateRange?: { startDate: Date; endDate: Date } | null;
  initialComparableData?: any[];
  onPropertyCorrections?: (corrections: {
    livingAreaSqft?: number;
    buildingSqft?: number;
    garageSize?: string;
  }) => void;
  propertyCorrections?: {
    livingAreaSqft?: number;
    buildingSqft?: number;
    garageSize?: string;
  };
}

const ComparableSales: React.FC<ComparableSalesProps> = ({
  userValuation,
  comparableSearchCriteria = null,
  subjectPropertySqft = 0,
  dateRange = null,
  initialComparableData = [],
  propertyCorrections,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [query] = useSearchParams();

  const agentIdentifier = useMemo(() => {
    const agent = query.get('agent');
    if (agent) window.localStorage.setItem('agent', agent.replace(' ', '+'));
    const localAgent = window.localStorage.getItem('agent');
    return localAgent;
  }, [query]);

  const { data: agent } = useGetAgent(agentIdentifier);

  const [isLoading, setIsLoading] = useState(false);
  const [compsFound, setCompsFound] = useState<boolean | null>(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [discardedComps, setDiscardedComps] = useState<string[]>([]);
  const [aiComps, setAiComps] = useState<IComparableProperty[]>([]);
  const [comps, setComps] = useState<IComparableProperty[]>([]);
  const [displayedComps, setDisplayedComps] = useState<IComparableProperty[]>(
    [],
  );

  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [showValuationModal, setShowValuationModal] = useState(false);

  // Add carousel API state
  const [carouselApi, setCarouselApi] = useState<any>(null);

  // New state for adjustment rate per sqft with proper default
  const [adjustmentRatePerSqft, setAdjustmentRatePerSqft] =
    useState<number>(200); // Default to $200
  const [isEditingRate, setIsEditingRate] = useState(false);

  // Effect to handle initial comparable data
  useEffect(() => {
    if (initialComparableData && initialComparableData.length > 0) {
      const transformedComps = transformApiComparables(initialComparableData);

      const average = transformedComps.reduce(
        (a, b) => a + b.soldPrice / transformedComps.length,
        0,
      );

      const filteredComps = transformedComps.filter(
        (c) =>
          c.soldPrice === 0 ||
          (c.soldPrice >= average * 0.7 && c.soldPrice <= average * 1.3),
      );

      setComps(filteredComps);
      setDisplayedComps(filteredComps);
      setCompsFound(true);
    }
  }, [initialComparableData]);

  const showAiButton = useMemo(() => comps.some((comp) => !!comp.mls), [comps]);

  // Effect to calculate initial adjustment rate per sqft using tiered structure
  useEffect(() => {
    console.log('Setting adjustment rate, userValuation:', userValuation);

    // Calculate the initial rate based on userValuation with proper fallbacks
    let initialRate = 200; // Default rate if no userValuation

    if (userValuation && userValuation > 0) {
      if (userValuation < 500000) {
        initialRate = 200;
      } else if (userValuation >= 500000 && userValuation < 1000000) {
        initialRate = 300;
      } else if (userValuation >= 1000000 && userValuation < 2000000) {
        initialRate = 400;
      } else if (userValuation >= 2000000) {
        initialRate = 450;
      }
    }

    console.log('Calculated adjustment rate:', initialRate);
    setAdjustmentRatePerSqft(initialRate);
  }, [userValuation]);

  // Function to call the compare API
  const fetchComparableProperties = async (
    criteria: ComparableSearchCriteria,
    saleDateMin: string,
    saleDateMax: string,
  ) => {
    try {
      // Build the payload with the new structure including sale dates
      const payload = {
        buildingSquareFeet: criteria.livingSquareFeet,
        yearBuilt: criteria.yearBuilt,
        propertyType: criteria.propertyType,
        propertyUseCode: criteria.propertyUseCode,
        longitude: criteria.longitude,
        latitude: criteria.latitude,
        county: criteria.county || '',
        state: criteria.state || '',
        saleDateMin: saleDateMin,
        saleDateMax: saleDateMax,
      };

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/properties/compare`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch comparable properties');
      }

      const data = await response.json();

      // Transform the API response data
      if (data && data.data && Array.isArray(data.data)) {
        return transformApiComparables(data.data);
      }

      return [];
    } catch (error) {
      console.error('Error fetching comparable properties:', error);
      throw error;
    }
  };

  // Handle starting the search process - now only needed if no initial data
  const handleStartSearch = async () => {
    if (!comparableSearchCriteria) {
      console.error('No comparable search criteria available');
      return;
    }

    if (!dateRange) {
      console.error('No date range available');
      return;
    }

    setIsLoading(true);
    setCurrentMessageIndex(0);
    setCompsFound(null);
    setDiscardedComps([]);

    // Set up message rotation
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => {
        if (prevIndex < progressMessages.length - 1) {
          return prevIndex + 1;
        }
        return prevIndex;
      });
    }, 700); // Change message every 700ms

    try {
      // Format dates for API call using the passed date range
      const saleDateMin = dateRange.startDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      const saleDateMax = dateRange.endDate.toISOString().split('T')[0]; // YYYY-MM-DD format

      // Call the compare API with date range
      const comparableData = await fetchComparableProperties(
        comparableSearchCriteria,
        saleDateMin,
        saleDateMax,
      );

      if (comparableData) {
        clearInterval(messageInterval);
        // Check if comps were found
        const found = comparableData.length > 0;
        setCompsFound(found);
        setComps(comparableData);
        setDisplayedComps(comparableData);
      }
    } catch (error) {
      clearInterval(messageInterval);
      setCompsFound(false);
      console.error('Failed to fetch comparable properties:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscardComp = (compId: string) => {
    setDiscardedComps((prev) => [...prev, compId]);
    setDisplayedComps((prev) => prev.filter((comp) => comp.id !== compId));
    setAiComps((prev) => prev.filter((c) => c.id !== compId));
  };

  const handleToggleAiComp = () => {
    const filteredComps = [...displayedComps].filter(
      (comp) => comp?.soldPrice !== null && !!comp.mls?.Media?.length,
    );
    const compLenght = filteredComps.length;

    const lowestThreeComps = filteredComps.slice(
      0,
      compLenght >= 3 ? 3 : compLenght,
    );

    setAiComps(lowestThreeComps);

    const propertyInfo = JSON.parse(
      window.localStorage.getItem('propertyInfo') || null,
    );

    if (!propertyInfo?.mls?.Media) {
      setShowAddImageModal(true);
    } else {
      setShowValuationModal(true);
    }
  };

  // Handle restoring all discarded comps
  const handleRestoreAll = () => {
    setDiscardedComps([]);
    setDisplayedComps(comps);
  };

  const handleCreateReport = ({
    fairMarketValue,
    officialValuation,
  }: {
    officialValuation?: string;
    fairMarketValue?: string;
  }) => {
    if (!aiComps.length) {
      window.localStorage.setItem(
        'comparableProperties',
        JSON.stringify(displayedComps),
      );
    } else {
      window.localStorage.setItem('comparableProperties', JSON.stringify([]));
    }

    window.localStorage.setItem('aiProperties', JSON.stringify(aiComps));

    if (fairMarketValue)
      window.localStorage.setItem('fairMarketValue', fairMarketValue);
    if (officialValuation)
      window.localStorage.setItem('officialValuation', officialValuation);

    if (!isAuthenticated) {
      setShowLoginDialog(true);
    } else {
      navigate('/dashboard?tab=reports');
    }
  };

  const handlePrepareAppeal = () => {
    const propertyInfo = JSON.parse(
      window.localStorage.getItem('propertyInfo') || null,
    );

    if (!propertyInfo?.mls?.Media && !!aiComps.length) {
      setShowAddImageModal(true);
    } else {
      setShowValuationModal(true);
    }
  };

  const handleCancelAddImages = () => {
    setAiComps([]);
  };

  const handleAdjustField = (field: string, value: string, id: string) => {
    const newComps = [...displayedComps];
    const adjustedIndex = newComps.findIndex((comp) => comp.id === id);
    newComps[adjustedIndex] = { ...newComps[adjustedIndex], [field]: value };
    setDisplayedComps(newComps);

    const newAIComps = [...aiComps];
    const aiIndex = newAIComps.findIndex((comp) => comp.id === id);
    if (aiIndex !== -1) {
      newAIComps[aiIndex] = { ...newAIComps[aiIndex], [field]: value };
      setAiComps(newAIComps);
    }
  };

  const handleAdjustPrice = (value: number, id: string) => {
    const newComps = [...displayedComps];
    const adjustedIndex = newComps.findIndex((comp) => comp.id === id);
    newComps[adjustedIndex] = {
      ...newComps[adjustedIndex],
      adjustmentValue: value,
    };
    setDisplayedComps(newComps);

    const newAIComps = [...aiComps];
    const aiIndex = newAIComps.findIndex((comp) => comp.id === id);
    if (aiIndex !== -1) {
      newAIComps[aiIndex] = { ...newAIComps[aiIndex], adjustmentValue: value };
      setAiComps(newAIComps);
    }
  };

  const handleAdjustNotes = (value: string, id: string) => {
    const newComps = [...displayedComps];
    const adjustedIndex = newComps.findIndex((comp) => comp.id === id);
    newComps[adjustedIndex] = {
      ...newComps[adjustedIndex],
      notes: value,
    };
    setDisplayedComps(newComps);

    const newAIComps = [...aiComps];
    const aiIndex = newAIComps.findIndex((comp) => comp.id === id);
    if (aiIndex !== -1) {
      newAIComps[aiIndex] = { ...newAIComps[aiIndex], notes: value };
      setAiComps(newAIComps);
    }
  };

  return (
    <div className="mt-8">
      {isLoading && (
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-pulse mb-4">
              <div className="text-4xl mb-2">
                {progressMessages[currentMessageIndex].icon}
              </div>
            </div>
            <h3 className="text-xl font-medium text-tax-deepBlue mb-2">
              Finding Comparable Sales
            </h3>
            <p className="text-tax-neutralGray mb-6">
              {progressMessages[currentMessageIndex].message}
            </p>
            <div className="w-full max-w-md">
              <div className="h-2 bg-muted rounded-full">
                <div
                  className="h-2 bg-primary rounded-full animate-pulse"
                  style={{
                    width: `${
                      ((currentMessageIndex + 1) / progressMessages.length) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {compsFound === false && !isLoading && (
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="text-center">
            <h3 className="text-xl font-medium text-tax-deepBlue mb-4">
              You've got a unique property!
            </h3>
            <p className="text-tax-neutralGray mb-6">
              We couldn't find any comparable sales. Let's call in a local
              expert.
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-6">
              <div className="w-32 h-32 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src={sponsoringAgent.photo}
                  alt={sponsoringAgent.name}
                  className="object-cover w-full h-full"
                />
              </div>
              <div className="text-left">
                <h4 className="text-lg font-medium">{sponsoringAgent.name}</h4>
                <p className="text-tax-neutralGray mb-1">
                  Local Property Tax Expert
                </p>
                <p className="mb-1">{sponsoringAgent.phone}</p>
                <p className="text-tax-brightBlue">{sponsoringAgent.email}</p>
              </div>
            </div>

            <Button
              className="bg-tax-brightBlue hover:bg-tax-brightBlue/90 text-white"
              onClick={() =>
                (window.location.href = `mailto:${sponsoringAgent.email}`)
              }
            >
              Contact Now
            </Button>
          </div>
        </div>
      )}

      {compsFound === true && !isLoading && displayedComps.length > 0 && (
        <div className="mt-4">
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            {/* Adjustment Rate Section */}
            <div className="mb-4 p-3 bg-tax-lightGray rounded-lg border">
              {showAiButton && (
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-blue-100 border-blue-500 text-blue-700 hover:bg-blue-200 w-full"
                  onClick={handleToggleAiComp}
                  disabled={!displayedComps.length}
                >
                  <WiStars />
                  <span>Use AI to create my report.</span>
                  <ArrowRight className="h-5 w-5" />
                </Button>
              )}
              {/* <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <span className="text-sm text-tax-neutralGray">
                    We'll adjust the comparable properties' value based on
                    square footage using{' '}
                    {isEditingRate ? (
                      <Input
                        type="number"
                        value={adjustmentRatePerSqft}
                        onChange={(e) =>
                          setAdjustmentRatePerSqft(Number(e.target.value))
                        }
                        onBlur={() => setIsEditingRate(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setIsEditingRate(false);
                          }
                        }}
                        className="inline-block w-20 h-6 px-1 text-xs mx-1"
                        autoFocus
                      />
                    ) : (
                      <span className="font-medium">
                        ${adjustmentRatePerSqft}
                      </span>
                    )}{' '}
                    per sqft.
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingRate(true)}
                  className="h-6 px-2 text-xs text-tax-brightBlue hover:text-tax-brightBlue/80"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              </div> */}
            </div>

            {/* Top Navigation Section */}
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => carouselApi?.scrollPrev()}
                className="h-10 w-10 bg-tax-brightBlue hover:bg-tax-brightBlue/90 border-tax-brightBlue text-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>

              <h3 className="text-xl font-medium text-tax-deepBlue text-center">
                Comparable Properties
              </h3>

              <Button
                variant="outline"
                size="icon"
                onClick={() => carouselApi?.scrollNext()}
                className="h-10 w-10 bg-tax-brightBlue hover:bg-tax-brightBlue/90 border-tax-brightBlue text-white"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            <div className="relative">
              {/* Swipe hint zones - only show on mobile */}
              <div className="md:hidden absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black/5 to-transparent pointer-events-none z-10 flex items-center justify-start pl-2">
                <div className="text-xs text-gray-400 opacity-70">←</div>
              </div>
              <div className="md:hidden absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black/5 to-transparent pointer-events-none z-10 flex items-center justify-end pr-2">
                <div className="text-xs text-gray-400 opacity-70">→</div>
              </div>

              <Carousel className="w-full" setApi={setCarouselApi}>
                <CarouselContent>
                  {displayedComps.map((comp, index) => (
                    <ComparableProperty
                      comp={comp}
                      index={index}
                      adjustmentRatePerSqft={adjustmentRatePerSqft}
                      onDiscardComp={handleDiscardComp}
                      totalItems={displayedComps.length}
                      subjectPropertySqft={subjectPropertySqft}
                      propertyCorrections={propertyCorrections}
                      onAdjustPrice={(value: number) =>
                        handleAdjustPrice(value, comp.id)
                      }
                      onNote={(val: string) => handleAdjustNotes(val, comp.id)}
                      onAdjustField={(field: string, value: any) =>
                        handleAdjustField(field, value, comp.id)
                      }
                    />
                  ))}
                </CarouselContent>
              </Carousel>
            </div>

            {/* Bottom Navigation Section */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <Button
                variant="outline"
                size="icon"
                onClick={() => carouselApi?.scrollPrev()}
                className="h-10 w-10 bg-tax-brightBlue hover:bg-tax-brightBlue/90 border-tax-brightBlue text-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={() => carouselApi?.scrollNext()}
                className="h-10 w-10 bg-tax-brightBlue hover:bg-tax-brightBlue/90 border-tax-brightBlue text-white"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            {discardedComps.length > 0 && (
              <div className="mt-4 text-center">
                <Button
                  variant="outline"
                  onClick={handleRestoreAll}
                  className="text-tax-brightBlue border-tax-brightBlue hover:bg-tax-lightGray"
                >
                  Restore All Comps ({discardedComps.length})
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Appeal Preparation button - moved outside the main card */}
      {initialComparableData?.length ? (
        <div className="mt-6 flex gap-2">
          <Button
            onClick={handlePrepareAppeal}
            className="w-full md:max-w-md md:mx-auto text-lg flex items-center justify-center gap-2"
            disabled={!displayedComps.length}
          >
            <div className="hidden md:flex items-center justify-center bg-primary text-primary-foreground rounded-full w-7 h-7 mr-2 font-medium">
              4
            </div>
            Prepare Appeal Report
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg py-6 px-4 lg:px-6 shadow-sm">
          {agent
            ? `Please contact your agent, ${agent.firstName} ${agent.lastName}, at tel. ${agent.phoneNumber} or email ${agent.emailAddress} and they can use the MLS to help provide comparable data to support your tax appeal.`
            : `Well… this is awkward. We couldn’t find any comparable sales to
          support the analysis, so we’re unable to complete it at this time.`}
        </div>
      )}

      <MultiImageUploader
        open={showAddImageModal}
        setOpen={setShowAddImageModal}
        onSuccess={() => setShowValuationModal(true)}
        onCancel={handleCancelAddImages}
      />
      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Your Appeal Report is Ready!</DialogTitle>
            <DialogDescription>
              Great job, we've finished preparing instructions for you and a
              detailed analysis for you to submit as part of your protest.
              Please create your free account so this can be saved and we will
              deliver the reports to your dashboard for download.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Button
              onClick={() => {
                setShowLoginDialog(false);

                loginWithRedirect({
                  appState: {
                    returnTo: '/dashboard?tab=reports',
                  },
                  authorizationParams: {
                    screen_hint: 'signup',
                  },
                });
              }}
              className="w-full max-w-xs mx-auto min-h-[3rem] bg-tax-brightBlue hover:bg-tax-brightBlue/90"
            >
              Create Free Account
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ValuationModal
        isOpen={showValuationModal}
        setIsOpen={() => setShowValuationModal(false)}
        onSubmit={handleCreateReport}
      />
      {/* <LoadingModal show={createLoading} /> */}
    </div>
  );
};

export default ComparableSales;
